import { useMemo } from "react";

import { useQuery } from "@tanstack/react-query";

import { useCurrentWorkspace, useFlow } from "src/api/queries";
import { buildLiveAnalyticsAgentContext } from "src/flow/analytics/m1/liveAnalyticsAgentContext";
import { fetchPreflightBlocks } from "src/flow/analytics/m1/workspaceSchemaSnapshot";

/** The `structured_input` for the first message of a live-analytics conversation,
 * shared through the query cache by every consumer. `null` while loading. */
export const useLiveAnalyticsPreflight = ({
  flowId,
  flowName,
  enabled = true,
}: {
  flowId: string;
  flowName: string;
  enabled?: boolean;
}): {
  structuredInput: Record<string, unknown> | null;
  flowVersionsResolved: boolean;
} => {
  // Same source the chart's UUID→name relabeling uses; archived versions are
  // included because recent decisions can reference them.
  const flowQuery = useFlow(flowId, {
    includeArchivedFlowVersions: true,
    enabled,
  });
  // Names are truncated to bound prompt size.
  const flowVersionIdToName = useMemo(
    () =>
      Object.fromEntries(
        (flowQuery.data?.versions ?? []).map(
          (v) => [v.id, v.name.slice(0, 40)] as const,
        ),
      ),
    [flowQuery.data?.versions],
  );
  // Version names gate the snapshot, which carries them as data. Errors
  // release the gate — names just fall back to UUIDs.
  const flowVersionsResolved = flowQuery.isSuccess || flowQuery.isError;

  const { data: workspace } = useCurrentWorkspace();
  const baseUrl = workspace?.base_url;

  const contextQuery = useQuery({
    queryKey: ["liveAnalyticsAgentContext", baseUrl, flowId],
    queryFn: () =>
      fetchPreflightBlocks(baseUrl as string, flowId, flowVersionIdToName),
    enabled: enabled && Boolean(baseUrl) && flowVersionsResolved,
    staleTime: Infinity,
    retry: 1,
    // the global default sends a 500 to the page's error boundary; this query degrades instead
    throwOnError: false,
  });

  // On failure the flow context still goes, without the snapshot: an empty
  // `flow_versions` would assert the flow has had no versions in 30 days, and a
  // zeroed schema that the flow has no keys. Omitted, the agent discovers keys
  // on demand — which is what the prompt tells it to do.
  const structuredInput = contextQuery.isError
    ? { flow_id: flowId, flow_name: flowName }
    : contextQuery.data
      ? buildLiveAnalyticsAgentContext({
          flowId,
          flowName,
          workspaceSchema: contextQuery.data.workspaceSchema,
          flowVersions: contextQuery.data.flowVersions,
        })
      : null;

  return { structuredInput, flowVersionsResolved };
};
